import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import WhatsAppButton from '@/components/layout/WhatsAppButton';
import { blogPosts } from '@/data/mock';
import usePageTitle from '@/hooks/usePageTitle';

const InsightsPage = () => {
  usePageTitle("Insights", "IT consulting insights, articles, and guides for UAE businesses. Expert perspectives on ERP, cybersecurity, cloud, AI, and digital transformation from ElitePlus Solutions.");

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-600 text-sm font-medium mb-6">
            Insights & Articles
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 mb-6">
            IT Consulting <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">Insights</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Expert perspectives on IT strategy, ERP, cybersecurity, cloud, AI, and digital transformation for UAE businesses.
          </p>
        </div>
      </section>

      {/* Featured Post */}
      {blogPosts.length > 0 && (
        <section className="relative pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to={`/insights/${blogPosts[blogPosts.length - 1].slug}`} className="group block">
              <article className="grid lg:grid-cols-2 gap-8 p-6 bg-white border border-slate-200 rounded-3xl hover:border-cyan-300 hover:shadow-xl hover:shadow-cyan-500/10 transition-all duration-300">
                <div className="overflow-hidden rounded-2xl">
                  <img
                    src={blogPosts[blogPosts.length - 1].image}
                    alt={`${blogPosts[blogPosts.length - 1].title} - IT consulting UAE`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-64 lg:h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex flex-col justify-center py-4">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-3 py-1 bg-cyan-500/10 text-cyan-600 text-xs font-medium rounded-full">{blogPosts[blogPosts.length - 1].category}</span>
                    <span className="text-slate-400 text-sm flex items-center gap-1"><Clock className="w-3 h-3" />{blogPosts[blogPosts.length - 1].readTime}</span>
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-slate-800 mb-4 group-hover:text-cyan-600 transition-colors">{blogPosts[blogPosts.length - 1].title}</h2>
                  <p className="text-slate-600 leading-relaxed mb-4">{blogPosts[blogPosts.length - 1].excerpt}</p>
                  <span className="text-cyan-500 font-medium flex items-center gap-2 group-hover:gap-3 transition-all">
                    Read Article <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </article>
            </Link>
          </div>
        </section>
      )}

      {/* Blog Grid */}
      <section className="relative pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogPosts.slice(0, -1).reverse().map((post) => (
              <Link key={post.id} to={`/insights/${post.slug}`} className="group">
                <article className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-cyan-300 hover:shadow-xl hover:shadow-cyan-500/10 transition-all duration-300 h-full flex flex-col" data-testid={`blog-card-${post.id}`}>
                  <div className="overflow-hidden">
                    <img
                      src={post.image}
                      alt={`${post.title} - ElitePlus IT consulting UAE`}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-2.5 py-1 bg-cyan-500/10 text-cyan-600 text-xs font-medium rounded-full">{post.category}</span>
                      <span className="text-slate-400 text-xs flex items-center gap-1"><Clock className="w-3 h-3" />{post.readTime}</span>
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-3 group-hover:text-cyan-600 transition-colors leading-snug">{post.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed flex-grow">{post.excerpt}</p>
                    <div className="flex items-center gap-1 mt-4 text-cyan-500 text-sm font-medium group-hover:gap-2 transition-all">
                      Read More <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-800 mb-4">
            Need Expert <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">IT Guidance</span>?
          </h2>
          <p className="text-slate-500 text-lg mb-8">
            Our senior consultants can help your UAE business navigate technology transformation.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold px-8" data-testid="insights-cta-btn">
              Book a Free Consultation <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default InsightsPage;
